import { NORMALIZED_TAG_TO_ANNOTATION_TYPE } from "@perceptron/core/types";
import { defaultSchema, type Options as SanitizeSchema } from "rehype-sanitize";

import { TOOL_CALL_SLOT_TAG } from "../tool-calls/ToolCallSlot";

/** Tags the model emits (after the remark rewrites) that have a component of their own. */
const ANNOTATION_TAG_NAMES = Object.keys(NORMALIZED_TAG_TO_ANNOTATION_TYPE);

/** The attributes the annotation parser reads. */
const ANNOTATION_ATTRIBUTES = ["mention", "t"];

/**
 * Elements removed together with their content. The HTML parser reads everything after an unclosed one of
 * these as its text, so unwrapping would show the rest of the answer as literal markup. Anything else outside
 * the allowlist is unwrapped so its text still reads as prose.
 */
const STRIPPED_TAG_NAMES = [
  "script",
  "style",
  "iframe",
  "frame",
  "frameset",
  "object",
  "embed",
  "noscript",
  "noembed",
  "noframes",
  "template",
  "textarea",
  "title",
  "xmp",
  "plaintext",
  "link",
  "meta",
  "base",
];

/** Allowed by the GitHub-style default but not wanted in a model answer: a remote image is a request the viewer never made. */
const EXCLUDED_DEFAULT_TAG_NAMES = new Set(["img", "picture", "source"]);

const TABLE_CELL_ATTRIBUTES = ["align", "colSpan", "rowSpan"];

/**
 * Allowlist for HTML in model output. `rehype-raw` turns whatever markup the model writes into real DOM, so
 * this runs straight after it: the GitHub-style default schema plus the annotation and tool-call tags and the
 * attributes they read. The rehype plugins that follow add their own properties (`number`, `data-*`,
 * highlight classes, fade spans) after sanitising, so nothing they set is subject to this list.
 *
 * The default's global attribute list is replaced with nothing: the annotation components spread their
 * properties onto the DOM, so a global `title` or `tabIndex` would otherwise reach the chip. The few globals
 * markdown needs are given to the tags that need them.
 */
export const modelOutputSanitizeSchema: SanitizeSchema = {
  ...defaultSchema,
  tagNames: [
    ...(defaultSchema.tagNames ?? []).filter((tagName) => !EXCLUDED_DEFAULT_TAG_NAMES.has(tagName)),
    ...ANNOTATION_TAG_NAMES,
    "think",
    TOOL_CALL_SLOT_TAG,
  ],
  attributes: {
    ...defaultSchema.attributes,
    "*": [],
    td: TABLE_CELL_ATTRIBUTES,
    th: TABLE_CELL_ATTRIBUTES,
    ol: [...(defaultSchema.attributes?.ol ?? []), "start"],
    input: [...(defaultSchema.attributes?.input ?? []), "checked"],
    details: ["open"],
    ...Object.fromEntries(ANNOTATION_TAG_NAMES.map((tagName) => [tagName, ANNOTATION_ATTRIBUTES])),
    [TOOL_CALL_SLOT_TAG]: ["at"],
  },
  strip: STRIPPED_TAG_NAMES,
};
